import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import {
  Box,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
  Snackbar,
  Alert,
} from "@mui/material";
import { ResponsiveNavbar } from "../../../components/ResponsiveNavbar/ResponsiveNavbar";
import jessProfileIcon from "../../../assets/navbar/iconperfiljesss.svg";
import jessProfileIconOff from "../../../assets/navbar/iconperfiljesssOff.svg";
import jessNameLogo from "../../../assets/navbar/nomejesss.svg";
import { Task, TaskStatus, ExtendedTask } from "../../../types/tasks";
import TaskColumn from "../../../components/taskBoard/Column";
import { useSwipeable } from "react-swipeable";
import { motion, AnimatePresence } from "framer-motion";
import { DragDropContext, DropResult } from "@hello-pangea/dnd";
import { useTranslation } from "react-i18next";
import TaskDetailsModal from "../../../components/taskBoard/TaskDetailsModal";
import {
  connectToTaskBoardWebSocket,
  TaskBoardWebSocketConnection,
} from "../../../services/taskBoard";
import { TaskBoardWebSocketMessage } from "../../../services/taskBoard/types";
import { getLoggedinUser } from "../../../api/apiCore";
import axios from "axios";
import { WEBSOCKET_TOKEN_REFRESH_INTERVAL } from "../../../constants/process";
import { useChatJessWebSocket } from "../../../hooks/ChatJess/useChatJessWebSocket";
import NoTasksModal from "../../../components/taskBoard/NoTasksModal";
import {
  fetchAgentConnectionStatus,
  AgentConnectionStatus,
} from "../../../api/agent";
import { logger } from "../../../utils/logger";
import { useGlobalTheme } from "../../../context/GlobalThemeContext";

const COLUMN_ORDER: TaskStatus[] = ["todo", "doing", "feedback", "done"];
const taskBoardBackgroundColor = "#DDDDDD";
const VALID_COLUMNS = new Set(COLUMN_ORDER);

const columnColors: Record<TaskStatus, string> = {
  todo: "#0585fa", // Blue
  doing: "#e69d00", // Orange
  feedback: "#54398e", // Purple
  done: "#328f3c", // Green
};

const groupTasksByStatus = (
  tasks: Task[],
): Record<TaskStatus, ExtendedTask[]> => {
  const grouped: Record<TaskStatus, ExtendedTask[]> = {
    todo: [],
    doing: [],
    feedback: [],
    done: [],
  };

  tasks.forEach((task) => {
    if (VALID_COLUMNS.has(task.status)) {
      grouped[task.status].push(task as ExtendedTask);
    }
  });

  // Sort each group by updatedAt (or createdAt as fallback) in descending order
  Object.keys(grouped).forEach((status) => {
    grouped[status as TaskStatus].sort((a, b) => {
      const dateA = new Date(a.updatedAt || a.createdAt).getTime();
      const dateB = new Date(b.updatedAt || b.createdAt).getTime();
      return dateB - dateA; // descending order (most recent first)
    });
  });

  return grouped;
};

const TaskBoardPage: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const isLg = useMediaQuery(theme.breakpoints.up("lg"));
  const isXl = useMediaQuery(theme.breakpoints.up("xl"));
  const isWideScreen = useMediaQuery("(min-width: 1400px)");

  const isDarkTheme = useGlobalTheme();

  const toggleTheme = () => {
    const newTheme = !isDarkTheme;
    localStorage.setItem("theme", newTheme ? "dark" : "light");
    window.dispatchEvent(
      new CustomEvent("appThemeChange", { detail: { isDark: newTheme } }),
    );
  };

  const [agentConnectionStatus, setAgentConnectionStatus] =
    useState<AgentConnectionStatus | null>(null);

  // Function to check agent connection status
  const checkAgentConnectionStatus = useCallback(async () => {
    try {
      const status = await fetchAgentConnectionStatus();
      setAgentConnectionStatus(status);
    } catch (error) {
      logger.error("Error fetching agent connection status:", error);
      const loggedUser = await getLoggedinUser();
      const fallbackStatus: AgentConnectionStatus = {
        isAgentConnected: false,
        companyId: loggedUser?.companyId || "",
        timestamp: Date.now(),
      };
      setAgentConnectionStatus(fallbackStatus);
    }
  }, []);

  // Effect to check agent status on mount and periodically
  useEffect(() => {
    checkAgentConnectionStatus();
    const intervalId = setInterval(() => {
      checkAgentConnectionStatus();
    }, 30000);
    return () => clearInterval(intervalId);
  }, [checkAgentConnectionStatus]);

  const [tasksByStatus, setTasksByStatus] = useState<
    Record<TaskStatus, ExtendedTask[]>
  >({
    todo: [],
    doing: [],
    feedback: [],
    done: [],
  });

  const [activeTab, setActiveTab] = useState(0);
  const [direction, setDirection] = useState<number>(0); // Track animation direction: -1 (left), 1 (right), 0 (none)
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState<ExtendedTask | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage] = useState("");
  const [totalTasks, setHasTasks] = useState(-1);

  const user = useMemo(() => getLoggedinUser(), []);

  const socketRef = useRef<TaskBoardWebSocketConnection | null>(null);

  // ChatJess WebSocket for human support feedback
  const chatJessWebSocket = useChatJessWebSocket({
    onError: (error) => {},
  });

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (totalTasks === -1) {
        setHasTasks(0);
      }
    }, 2000);

    return () => {
      clearTimeout(timeout);
    };
  }, [totalTasks]);

  const refreshAccessToken = useCallback(
    async (options: { signal?: AbortSignal } = {}): Promise<boolean> => {
      try {
        await axios.post(
          "/api/auth/refresh",
          {},
          {
            withCredentials: true,
            signal: options.signal,
          },
        );
        return true;
      } catch (err) {
        logger.error("Failed to refresh access token", err);
        return false;
      }
    },
    [],
  );

  const connectWebSocket = useCallback(() => {
    if (!user) return;

    /* Close any previous connection */
    if (
      socketRef.current &&
      typeof socketRef.current.disconnect === "function"
    ) {
      socketRef.current.disconnect();
    }

    socketRef.current = connectToTaskBoardWebSocket({
      companyId: user?.companyID,
      onMessage: (message: TaskBoardWebSocketMessage) => {
        if (!message.data || !Array.isArray(message.data)) return;

        const groupedTasksByStatus = groupTasksByStatus(message.data);
        const total = Object.values(groupedTasksByStatus).reduce(
          (acc, curr) => acc + curr.length,
          0,
        );

        setHasTasks(total);
        setTasksByStatus(groupedTasksByStatus);
      },
      onConnectionChange: () => {},
      onError: async () => {},
      onStateChange: () => {},
    });
  }, [user]);

  /* Establish connection on mount and clean-up on unmount */
  useEffect(() => {
    const abortController = new AbortController();
    connectWebSocket();
    setIsLoading(false);

    const refreshTokenInterval = setInterval(async () => {
      try {
        const isRefreshed = await refreshAccessToken({
          signal: abortController.signal,
        });
        if (!abortController.signal.aborted && isRefreshed) {
          connectWebSocket();
        }
      } catch (error: any) {
        if (error.name !== "AbortError") {
          logger.error("Token refresh failed!");
        }
      }
    }, WEBSOCKET_TOKEN_REFRESH_INTERVAL);

    return () => {
      if (
        socketRef.current &&
        typeof socketRef.current.disconnect === "function"
      ) {
        socketRef.current.disconnect();
      }
      clearInterval(refreshTokenInterval);
    };
  }, [connectWebSocket, refreshAccessToken]);

  const handleDeleteTask = (taskId: string) => {
    setTasksByStatus((prev) => ({
      ...prev,
      done: prev.done.filter((task) => task.id !== taskId),
    }));
  };

  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result;
    if (!destination) return;

    const sourceStatus = source.droppableId as TaskStatus;
    const destStatus = destination.droppableId as TaskStatus;

    if (sourceStatus === destStatus) {
      const items = Array.from(tasksByStatus[sourceStatus]);
      const [reorderedItem] = items.splice(source.index, 1);
      items.splice(destination.index, 0, reorderedItem);
      setTasksByStatus((prev) => ({ ...prev, [sourceStatus]: items }));
    } else {
      const sourceItems = Array.from(tasksByStatus[sourceStatus]);
      const [removed] = sourceItems.splice(source.index, 1);
      const destItems = Array.from(tasksByStatus[destStatus]);
      destItems.splice(destination.index, 0, {
        ...removed,
        status: destStatus,
      });
      setTasksByStatus((prev) => ({
        ...prev,
        [sourceStatus]: sourceItems,
        [destStatus]: destItems,
      }));
    }
  };

  const handlers = useSwipeable({
    onSwipedLeft: () => {
      if (isMobile && activeTab < COLUMN_ORDER.length - 1) {
        setDirection(1);
        setActiveTab((prev) => prev + 1);
      }
    },
    onSwipedRight: () => {
      if (isMobile && activeTab > 0) {
        setDirection(-1);
        setActiveTab((prev) => prev - 1);
      }
    },
    preventScrollOnSwipe: true,
    trackMouse: true,
  });

  const themeSpacing = (value: number) => theme.spacing(value);
  let columnsLeftPadding: string | number = 0;
  if (isDesktop) {
    if (isXl) columnsLeftPadding = themeSpacing(20);
    else if (isLg) columnsLeftPadding = themeSpacing(16);
    else columnsLeftPadding = themeSpacing(8);
  }

  const columnFixedWidth = "17.5em";
  const columnGapValue = 3;
  const columnGap = themeSpacing(columnGapValue);

  let columnMinHeight = "500px";
  let columnMaxHeight = "calc(100vh - 180px)";
  if (isMobile) {
    columnMinHeight = "100%";
    columnMaxHeight = "100%";
  } else if (isTablet) {
    columnMinHeight = "450px";
    columnMaxHeight = "calc(100vh - 200px)";
  }

  const handleTaskClick = (task: ExtendedTask) => {
    setSelectedTask(task);
  };

  const handleCloseModal = () => {
    setSelectedTask(null);
  };

  const handleTakeOverTask = (
    taskId: string,
    reason: string,
    agentName?: string,
  ) => {
    if (!selectedTask || selectedTask.id !== taskId) return;

    setTasksByStatus((prev) => {
      const newTasksByStatus = { ...prev };
      let taskToMove: ExtendedTask | undefined;
      let originalStatus: TaskStatus | undefined;

      for (const status of COLUMN_ORDER) {
        const idx = newTasksByStatus[status].findIndex((t) => t.id === taskId);
        if (idx > -1) {
          originalStatus = status;
          [taskToMove] = newTasksByStatus[status].splice(idx, 1);
          break;
        }
      }

      if (taskToMove && originalStatus) {
        const updatedTask: ExtendedTask = {
          ...taskToMove,
          status: "done",
          takenOverByHuman: true,
        };
        newTasksByStatus.done = [updatedTask, ...newTasksByStatus.done];
        return newTasksByStatus;
      }

      return prev;
    });

    setSelectedTask(null);
    socketRef?.current?.sendMessage(
      {
        ...selectedTask,
        status: "done",
        taskType: "take_over",
      } as Task,
      "task",
    );
  };

  const handleGiveFeedback = (task: Task, stepId: string, feedback: string) => {
    chatJessWebSocket.sendTaskFeedback(task, stepId, feedback);
  };

  const handleCloseSnackbar = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        backgroundColor: isDarkTheme ? taskBoardBackgroundColor : "#EEEEEF",
        backdropFilter: selectedTask ? "blur(5px)" : "none",
        WebkitBackdropFilter: selectedTask ? "blur(5px)" : "none",
        transition: "backdrop-filter 0.3s ease",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        paddingTop: { xs: "60px", md: "80px" }, // Altura da navbar (mobile: 60px, desktop: 80px)
      }}
    >
      {/* Responsive Navbar */}
      <ResponsiveNavbar
        isConnected={false}
        connection={{ status: "disconnected", message: "" }}
        isDarkTheme={isDarkTheme}
        onThemeToggle={toggleTheme}
        isSidebarCollapsed={false}
        profileImage={
          agentConnectionStatus?.isAgentConnected
            ? jessProfileIcon
            : jessProfileIconOff
        }
        profileName={jessNameLogo}
        agentConnectionStatus={agentConnectionStatus}
      />
      <NoTasksModal
        open={totalTasks === 0}
        onClose={() => {
          setHasTasks(-1);
        }}
        agentConnectionStatus={agentConnectionStatus}
      />
      {isMobile && (
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => {
            setDirection(newValue > activeTab ? 1 : -1);
            setActiveTab(newValue);
          }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            mb: "-0.125em",
            borderBottom: `0.0625em solid ${theme.palette.divider}`,
            flexShrink: 0,
            px: 0,

            "& .MuiTabs-scroller": {
              paddingLeft: 0,
            },

            "& .MuiTabs-flexContainer": {
              paddingLeft: 0,
              marginLeft: 0,
              gap: "1.5em",
              display: "flex",
              justifyContent: "space-between",
            },

            "& .MuiTab-root": {
              minWidth: 0,
              px: "0.75em",
              textTransform: "uppercase",
              fontWeight: 550,
              fontSize: "0.8em",
              color: theme.palette.text.secondary,
              marginRight: "0.5em",
              "&:last-child": {
                marginRight: 0,
              },
            },

            "& .MuiTab-root.Mui-selected": {
              color: columnColors[COLUMN_ORDER[activeTab]],
            },

            "& .MuiTabs-indicator": {
              display: "none", // Hide default indicator since we're using border on Tab
            },
          }}
        >
          {COLUMN_ORDER.map((status, idx) => (
            <Tab
              key={status}
              label={t(`status.${status}`)}
              sx={{
                ml: idx === 0 ? 0 : undefined,
                color:
                  activeTab === idx
                    ? columnColors[status]
                    : theme.palette.text.secondary,
                borderBottom:
                  activeTab === idx
                    ? `2px solid ${columnColors[status]}`
                    : "2px solid transparent",
                fontWeight: activeTab === idx ? 600 : 550,
              }}
            />
          ))}
        </Tabs>
      )}

      <DragDropContext onDragEnd={onDragEnd}>
        <Box
          {...handlers}
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "flex-start",
            alignItems: isMobile ? "stretch" : "flex-start",
            overflow: "hidden",
            width: "100%",
            pt: isMobile ? 0 : isTablet ? "20px" : 10,
            pb: isMobile ? 0 : 4,
            // Remove horizontal padding from parent - it will be on the scrollable child instead
            pl: isMobile ? 0 : 0,
            pr: isMobile ? 0 : 0,
            boxSizing: "border-box",
            minHeight: 0, // Important for flex children to shrink properly
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              flexWrap: isTablet ? "wrap" : "nowrap",
              alignItems: isMobile ? "stretch" : "flex-start",
              // Enable horizontal scrolling for medium screens (between tablet and desktop) and desktop
              overflowX: isTablet ? "hidden" : "auto",
              overflowY: isTablet ? "auto" : "hidden", // Enable vertical scrolling on tablet
              width: "100%",
              gap: isMobile ? 0 : columnGap,
              pb: isDesktop ? 2 : 0,
              justifyContent: isWideScreen ? "center" : "flex-start",
              flex: isMobile ? 1 : "none",
              // Calculate height accounting for navbar and padding
              // On tablet: navbar (60px) + handlers top padding (20px) + handlers bottom padding (32px)
              height: isTablet
                ? `calc(100vh - 60px - 20px - ${themeSpacing(4)})`
                : undefined,
              minHeight: 0, // Important for flex children to shrink properly
              position: isMobile ? "relative" : "static",
              // Add padding inside scrollable container to ensure first/last columns are fully visible
              paddingLeft: isTablet ? 0 : columnsLeftPadding,
              paddingRight: isTablet ? 0 : columnsLeftPadding,
              // Smooth scrolling
              scrollBehavior: "smooth",
              // Add scrollbar styling for better UX
              "&::-webkit-scrollbar": {
                height: "8px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: isDarkTheme
                  ? "rgba(255, 255, 255, 0.3)"
                  : "rgba(0, 0, 0, 0.3)",
                borderRadius: "4px",
                "&:hover": {
                  backgroundColor: isDarkTheme
                    ? "rgba(255, 255, 255, 0.5)"
                    : "rgba(0, 0, 0, 0.5)",
                },
              },
            }}
          >
            <AnimatePresence mode="sync" initial={false}>
              {COLUMN_ORDER.map((status, index) => {
                if (isMobile && index !== activeTab) return null;
                const columnWidth = isMobile
                  ? "100%"
                  : isTablet
                    ? `calc(50% - ${themeSpacing(columnGapValue / 2)})`
                    : columnFixedWidth;

                // Calculate slide direction for mobile - smooth horizontal slide
                const slideDistance = isMobile ? "100%" : "50px";
                const initialX = isMobile
                  ? direction === 1
                    ? slideDistance
                    : direction === -1
                      ? `-${slideDistance}`
                      : 0
                  : 50;
                const exitX = isMobile
                  ? direction === 1
                    ? `-${slideDistance}`
                    : direction === -1
                      ? slideDistance
                      : 0
                  : -50;

                return (
                  <motion.div
                    key={`${status}-${activeTab}`}
                    initial={{
                      opacity: 0,
                      x: initialX,
                    }}
                    animate={{
                      opacity: 1,
                      x: 0,
                    }}
                    exit={{
                      opacity: 0,
                      x: exitX,
                    }}
                    transition={{
                      duration: 0.25,
                      ease: [0.25, 0.1, 0.25, 1], // Smooth cubic-bezier easing
                    }}
                    style={{
                      width: columnWidth,
                      display: "flex",
                      flexDirection: "column",
                      flexShrink: 0,
                      height: isMobile ? "100%" : "auto",
                      position: isMobile ? "absolute" : "relative",
                      top: isMobile ? 0 : "auto",
                      left: isMobile ? 0 : "auto",
                      right: isMobile ? 0 : "auto",
                      bottom: isMobile ? 0 : "auto",
                    }}
                  >
                    <TaskColumn
                      status={status}
                      tasks={tasksByStatus[status]}
                      loading={isLoading}
                      minHeight={columnMinHeight}
                      maxHeight={columnMaxHeight}
                      onDeleteTask={handleDeleteTask}
                      onTaskClick={handleTaskClick}
                    />
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </Box>
        </Box>
      </DragDropContext>

      {selectedTask && (
        <TaskDetailsModal
          open={!!selectedTask}
          task={selectedTask}
          socketConnection={socketRef.current}
          chatJessWebSocket={chatJessWebSocket}
          onClose={handleCloseModal}
          onTakeOver={handleTakeOverTask}
          onGiveFeedback={handleGiveFeedback}
        />
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="warning"
          sx={{ width: "100%" }}
        >
          {snackbarMessage || t("operationNotAllowed")}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TaskBoardPage;
